import { observable, computed, action } from 'mobx';
import {
  API_URL,
  DATE_FORMAT,
  API_URL_MIDDLEWARE,
} from '../constants/constants';
import {
  Customer,
  NAVOrder,
  CartProduct,
  Notification as INotification,
  NavOrderResult,
} from '../models/interfaces';
import * as moment from 'moment';

import { NOTIFICATION_STORE } from '../constants/constants';
import { NotificationStore } from './NotificationStore';
import { CompanyStore } from './CompanyStore';

export class OrderFormStore {
  public notificationStore: NotificationStore;
  public companyStore: CompanyStore;

  @observable
  private orderForm: NAVOrder;

  @observable
  private massOrder: boolean;

  @observable
  private selectedCustomers: Array<Customer>;

  @observable
  private creatingOrder: boolean;

  constructor(
    notificationStore: NotificationStore,
    companyStore: CompanyStore
  ) {
    this.orderForm = new NAVOrder();
    this.massOrder = false;
    this.notificationStore = notificationStore;
    this.companyStore = companyStore;
    this.selectedCustomers = [];
    this.creatingOrder = false;
  }

  @computed
  get customers() {
    return this.selectedCustomers;
  }

  @computed
  get orderData() {
    return this.orderForm;
  }

  @computed
  get ordering() {
    return this.creatingOrder;
  }

  @action
  setOrdering(value: boolean) {
    this.creatingOrder = value;
  }

  @action
  setOrderType(orderType: string) {
    this.orderForm.ReasonCode = orderType;
    this.setLocalStorage();
  }

  
  @action
  setMassOrder(massOrder: boolean) {
    this.massOrder = massOrder;
    this.setLocalStorage();
  }

  @action
  clearOrderType() {
    this.orderForm.ReasonCode = '';
  }

  @action
  setCustomers(customers: Array<Customer>) {
    this.selectedCustomers = customers;
    this.setCustomersData();
  }

  @action
  addCustomer(customer: Customer) {
    this.setCustomers([...this.customers, customer]);
  }

  @action
  removeCustomer(customer: Customer, index: number) {
    this.setCustomers(this.customers.filter((c, i) => i !== index));
  }

  @action
  setCustomersData() {
    if (this.selectedCustomers.length === 1) {
      this.setCustomer(this.selectedCustomers[0]);
    } else if (this.selectedCustomers.length > 1) {
      this.selectedCustomers.map((customer, index) => {
        if (index === 0) {
          this.orderForm.CustomerId = customer.No;
        } else {
          this.orderForm.CustomerId = `${this.orderForm.CustomerId},${customer.No}`;
        }
        this.orderForm.Name = 'Useita asiakkaita';
        this.orderForm.Name2 = 'Useita asiakkaita';
        this.orderForm.ShipAddr1 = 'Useita asiakkaita';
        this.orderForm.ShipCity = 'Useita asiakkaita';
        this.orderForm.ShipZip = 'Useita asiakkaita';
      });
    } else {
      this.clearCustomer();
    }
    this.setLocalStorage();
  }

  @action
  setCustomer(customer: Customer) {
    this.orderForm.CustomerId = customer.No;
    this.orderForm.Name = customer.Name;
    this.orderForm.ShipAddr1 = customer.Address;
    this.orderForm.ShipCity = customer.City;
    this.orderForm.ShipZip = customer.Post_Code;

    if (customer.Name2) {
      this.orderForm.Name2 = customer.Name2;
    } else {
      this.orderForm.Name2 = '';
    }

    if (customer.Address2) {
      this.orderForm.ShipAddr2 = customer.Address2;
    } else {
      this.orderForm.ShipAddr2 = '';
    }
  }

  /**
   * Get customer data in NAV format
   * 
   * Doesn't alter the form.
   * @param customer Customer object to be parsed
   * @returns Customer information to be sent to NAV (customer data in nav format)
   */
  @action
  getParsedCustomer(customer: Customer): object {
    const parsedCustomer: any = {};

    parsedCustomer.CustomerId = customer.No;
    parsedCustomer.Name = customer.Name;
    parsedCustomer.ShipAddr1 = customer.Address;
    parsedCustomer.ShipCity = customer.City;
    parsedCustomer.ShipZip = customer.Post_Code;

    if (customer.Name2) {
      parsedCustomer.Name2 = customer.Name2;
    } else {
      parsedCustomer.Name2 = '';
    }

    if (customer.Address2) {
      parsedCustomer.ShipAddr2 = customer.Address2;
    } else {
      parsedCustomer.ShipAddr2 = '';
    }
    
    return parsedCustomer;
  }

  @action
  clearCustomer() {
    this.orderForm.CustomerId = '';
    this.orderForm.Name = '';
    this.orderForm.Name2 = '';
    this.orderForm.ShipAddr1 = '';
    this.orderForm.ShipAddr2 = '';
    this.orderForm.ShipCity = '';
    this.orderForm.ShipZip = '';
    this.selectedCustomers = [];
    this.setLocalStorage();
  }

  @action
  setCustomerRecipentName(customer: Customer, index: number, name: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.Name2 = name;
      }
      return c;
    });
  }

  @action
  setCustomerSecondAddress(customer: Customer, index: number, address: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.Address2 = address;
      }
      return c;
    });
  }

  @action
  setCustomerName(customer: Customer, index: number, name: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.Name = name;
      }
      return c;
    });
  }

  @action
  setCustomerAddress(customer: Customer, index: number, address: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.Address = address;
      }
      return c;
    });
  }

  @action
  setCustomerCity(customer: Customer, index: number, city: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.City = city;
      }
      return c;
    });
  }

  @action
  setCustomerZip(customer: Customer, index: number, zip: string) {
    this.selectedCustomers = this.customers.map((c, i) => {
      if (c.No === customer.No && index === i) {
        c.Post_Code = zip;
      }
      return c;
    });
  }

  @action
  setContactName(name: string) {
    this.orderForm.ShipContact = name;
    this.setLocalStorage();
  }

  @action
  setContactEmail(email: string) {
    this.orderForm.EMail = email;
    this.setLocalStorage();
  }

  @action
  setShipDate(date: string) {
    this.orderForm.ShipDate = date;
    this.setLocalStorage();
  }

  @action
  setOrderDate(date: string) {
    this.orderForm.OrderDate = date;
    this.setLocalStorage();
  }

  @action
  setOrderDetails(details: string) {
    this.orderForm.Reference = details;
    this.setLocalStorage();
  }

  @action
  setProducts(products: Array<CartProduct>) {
    this.orderForm.WLines.WebOrderLineWS = products;
    this.setLocalStorage();
  }

  @action
  setAccompanyingLetter(value: string) {
    this.orderForm.YourReference2 = value;
    this.setLocalStorage();
  }

  @action
  validateOrder() {
    let errors: Array<INotification> = [];

    if (this.orderForm.WLines.WebOrderLineWS.length === 0) {
      errors.push({
        message: 'Ostoskori tyhjä',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.CustomerId) {
      errors.push({
        message: 'Asiakasta ei valittu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.Name) {
      errors.push({
        message: 'Asiakkaan nimi puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ShipAddr1) {
      errors.push({
        message: 'Osoite puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ShipCity) {
      errors.push({
        message: 'Kaupunki puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ShipZip) {
      errors.push({
        message: 'Postinumero puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ShipContact) {
      errors.push({
        message: 'Tilaajan nimi puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ShipDate) {
      errors.push({
        message: 'Lähetyspäivä puuttuu',
        seen: false,
        type: 'error',
      });
    }

    if (!this.orderForm.ReasonCode) {
      errors.push({
        message: 'Ottotyyppi puuttuu',
        seen: false,
        type: 'error',
      });
    }

    this.selectedCustomers.map((customer) => {
      if (!customer.Name) {
        errors.push({
          message: 'Asiakkaan nimi puuttuu',
          seen: false,
          type: 'error',
        });
      }
      if (!customer.Address) {
        errors.push({
          message: 'Asiakkaan osoite puuttuu',
          seen: false,
          type: 'error',
        });
      }
      if (!customer.City) {
        errors.push({
          message: 'Asiakkaan kaupunki puuttuu',
          seen: false,
          type: 'error',
        });
      }
      if (!customer.Post_Code) {
        errors.push({
          message: 'Asiakkaan postinumero puuttuu',
          seen: false,
          type: 'error',
        });
      }
    });

    return errors;
  }

  /**
   * Order request is a content type in drupal, used mostly for debugging
   * It contains the raw order + customers as json
   * 
   * It is sent before the other order content types.
   */
  @action 
  async submitOrderRequest(products: Array<CartProduct>, customers: Customer[]) {
    const token = localStorage.getItem('token');
    const headers = new Headers();

    headers.append('Content-type', 'application/json');
    headers.append('X-CSRF-TOKEN', `${token}`);

    const company = this.companyStore.selectedCompany;
    const parsedCustomers: object[] = [];
    for (const customer of this.selectedCustomers) {
      parsedCustomers.push(this.getParsedCustomer(customer));
    };

    const payload: object = {
      data: {
        company: company,
        customers: parsedCustomers,
        order: this.orderData
      }
    };

    const results: { fetchResponse: Response }[] = [];
    results.push({
      fetchResponse: await fetch(`${API_URL}/v1/order_request?company=${company}`, {
        headers: headers,
        method: 'POST',
        body: JSON.stringify(payload),
        credentials: 'include', // remove comments when changing to drupal api
      })
    });

    return results;
  }

  @action
  async createOrder(
    products: Array<CartProduct>
  ): Promise<Array<{ order: NAVOrder; fetchResponse: Response }>> {
    this.setOrdering(true);
    const token = localStorage.getItem('token');
    this.orderForm.OrderDate = moment().format(DATE_FORMAT);
    this.orderForm.WLines.WebOrderLineWS = products;
    const company = this.companyStore.selectedCompany;
    const headers = new Headers();

    headers.append('Content-type', 'application/json');
    headers.append('X-CSRF-TOKEN', `${token}`);

    if (this.massOrder) {
      this.orderForm.Reference = "MASSA -- " + this.orderForm.Reference;
    }
    

    // Old way that causes problems with too much concurrency
    /*return Promise.all(this.selectedCustomers.map(async customer => {
      this.orderForm.OrderId = this.generateGUID();
      this.setCustomer(customer);

      this.orderData.EMail = this.orderData.EMail !== '' ? this.orderData.EMail : '-';

      const payload = { type: 'createOrder', orderData: this.orderData, company: company };

      let result = {
        order: { ...this.orderData },
        fetchResponse: await fetch(`${API_URL}/v1/orders?company=${company}`, {
          headers: headers,
          method: 'POST',
          body: JSON.stringify(payload),
          credentials: 'include' // remove comments when changing to drupal api
        })
      };

      return result;
    }));*/

    await this.submitOrderRequest(products, this.customers);

    // Safe, one-at-a-time, method:
    const results: { order: NAVOrder; fetchResponse: Response }[] = [];
    for (const customer of this.selectedCustomers) {
      this.orderForm.OrderId = this.generateGUID();
      this.setCustomer(customer);

      this.orderData.EMail =
        this.orderData.EMail !== '' ? this.orderData.EMail : '-';

      const payload = {
        type: 'createOrder',
        orderData: this.orderData,
        company: company,
      };

      results.push({
        order: { ...this.orderData },
        fetchResponse: await fetch(`${API_URL}/v1/orders?company=${company}`, {
          headers: headers,
          method: 'POST',
          body: JSON.stringify(payload),
          credentials: 'include', // remove comments when changing to drupal api
        }),
      });
    }

    return results;
  }

  @action
  async sendEmailConfirmation(orders: Array<NavOrderResult>) {
    let orderIds: Array<string> = [];

    orders.map(({ navOrder, status }) => {
      if (status === 'success') {
        orderIds = [...orderIds, navOrder.OrderId];
      }
    });

    const token = localStorage.getItem('token');
    const headers = new Headers();

    headers.append('Content-type', 'application/json');
    headers.append('X-CSRF-TOKEN', `${token}`);

    const payload = {
      products: this.orderData.WLines.WebOrderLineWS,
      customers: this.selectedCustomers,
      email: this.orderData.EMail,
      name: this.orderData.ShipContact,
      shipDate: moment(this.orderData.ShipDate).format('DD.MM.YYYY'),
      orderDate: moment(this.orderData.OrderDate).format('DD.MM.YYYY'),
      orderIds: orderIds,
    };

    fetch(`${API_URL}/v1/email`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify(payload),
      credentials: 'include',
    })
      .then((result) => {
        return result;
      })
      .catch(() => {
        return;
      });
  }

  @action
  clearStore() {
    const { EMail, ShipContact } = this.orderForm;

    this.clearLocalStorage();
    this.orderForm = new NAVOrder();

    this.setContactEmail(EMail);
    this.setContactName(ShipContact);
  }

  generateGUID() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return (
      s4() +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      s4() +
      s4()
    );
  }

  @action
  clearLocalStorage() {
    localStorage.removeItem('formData');
  }

  @action
  setLocalStorage() {
    localStorage.setItem('formData', JSON.stringify(this.orderForm));
  }

  @action
  getFromLocalStorage() {
    let storageString = localStorage.getItem('formData');
    if (storageString) {
      let parsedData: NAVOrder = JSON.parse(storageString);

      if (parsedData.CustomerId) {
        this.orderForm.CustomerId = parsedData.CustomerId;
      }
      if (parsedData.EMail) {
        this.orderForm.EMail = parsedData.EMail;
      }
      if (parsedData.Name) {
        this.orderForm.Name = parsedData.Name;
      }
      if (parsedData.Reference) {
        this.orderForm.Reference = parsedData.Reference;
      }
      if (parsedData.Reference2) {
        this.orderForm.Reference2 = parsedData.Reference2;
      }
      if (parsedData.YourReference2) {
        this.orderForm.YourReference2 = parsedData.YourReference2;
      }
      if (parsedData.ShipAddr1) {
        this.orderForm.ShipAddr1 = parsedData.ShipAddr1;
      }
      if (parsedData.ShipCity) {
        this.orderForm.ShipCity = parsedData.ShipCity;
      }
      if (parsedData.ShipZip) {
        this.orderForm.ShipZip = parsedData.ShipZip;
      }
      if (parsedData.ShipDate) {
        this.orderForm.ShipDate = parsedData.ShipDate;
      }
      if (parsedData.ShipContact) {
        this.orderForm.ShipContact = parsedData.ShipContact;
      }
      if (parsedData.ReasonCode) {
        this.orderForm.ReasonCode = parsedData.ReasonCode;
      }
    }
  }
}

export default OrderFormStore;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/OrderFormStore.ts